/**
 * DOCTRACK part 3 (design 4.6) - "Where these numbers came from" on the Property Database record: per field, the number
 * in use with who or what set it, and every other source's number with its date, its sentence and whether its document
 * is checked. "Use another" lets him put another number in use (it becomes his); a document that names another
 * property asks first, and his Yes or No answers it for good. A load that fails is one line, never the page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { FieldValueLine, FieldValuesView } from '../types';
import { Card, CardHeader, CardTitle, CardContent } from './ui';
import { FIELD_LABELS, alsoLabel, askLabel, formatFieldValue, whoLabel } from '../lib/fieldValues';

interface Props {
  masterPropertyId: string;
  /** The moment the dates are read against (tests pin it). */
  now?: Date;
  /** After a pick or an answer: the page re-reads the record's own numbers. */
  onChanged?: () => void;
  /** Each view as it loads. */
  onView?: (v: FieldValuesView) => void;
}

const LINK = 'font-medium text-red-600 hover:underline disabled:opacity-50';

/** A line he can pick on: another source's number, or the number in use while its document is not checked. */
const canPick = (line: FieldValueLine): boolean =>
  line.also.some((a) => !!a.id) || (!!line.inUse?.id && line.inUse.notChecked);

export default function FieldValuesPanel({ masterPropertyId, now, onChanged, onView }: Props) {
  const [view, setView] = useState<FieldValuesView | null>(null);
  const [failed, setFailed] = useState(false);
  const [picking, setPicking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // The page's callbacks change every render; the loads must not.
  const onViewRef = useRef(onView);
  const onChangedRef = useRef(onChanged);
  onViewRef.current = onView;
  onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    try {
      const r = await api.getFieldValues(masterPropertyId);
      const next: FieldValuesView = { masterPropertyId: r.masterPropertyId ?? masterPropertyId, lines: r.lines ?? [], asks: r.asks ?? [] };
      setView(next);
      setFailed(false);
      onViewRef.current?.(next);
    } catch {
      setFailed(true);
    }
  }, [masterPropertyId]);

  useEffect(() => { void load(); }, [load]);

  const act = async (run: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setProblem(null);
    try {
      await run();
      setPicking(null);
      await load();
      onChangedRef.current?.();
    } catch (e) {
      setProblem(e instanceof Error && e.message ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  };
  const pick = (valueId: string) => act(() => api.pickFieldValue(masterPropertyId, valueId), 'That number could not be used - try again');
  const answer = (askId: string, a: 'yes' | 'no') => act(() => api.answerValueAsk(masterPropertyId, askId, a), 'That answer could not be saved - try again');

  if (failed) return <p className="text-sm text-gray-500">Where these numbers came from could not be loaded</p>;
  if (!view || (view.lines.length === 0 && view.asks.length === 0)) return null;
  const at = now ?? new Date();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Where these numbers came from</CardTitle>
      </CardHeader>
      <CardContent>
        {problem && <p role="alert" className="mb-3 text-sm text-red-600">{problem}</p>}
        {view.asks.map((ask) => (
          <div key={ask.id} className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
            <p className="text-gray-800">{askLabel(ask)}</p>
            <div className="mt-2 flex gap-3">
              <button type="button" className={LINK} disabled={busy} onClick={() => answer(ask.id, 'yes')}>Yes</button>
              <button type="button" className={LINK} disabled={busy} onClick={() => answer(ask.id, 'no')}>No</button>
            </div>
          </div>
        ))}
        <dl className="space-y-4">
          {view.lines.map((line) => (
            <div key={line.field}>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{FIELD_LABELS[line.field]}</dt>
              <dd className="mt-1 flex flex-wrap items-baseline gap-x-2 text-sm text-gray-900">
                <span className="font-medium">{line.inUse ? formatFieldValue(line.field, line.inUse.value) : 'Not set'}</span>
                {line.inUse && <span className="text-gray-500">{`(${whoLabel(line.inUse, at)})`}</span>}
                {picking === line.field && line.inUse?.id && line.inUse.notChecked && (
                  <button type="button" className={LINK} disabled={busy} onClick={() => pick(line.inUse!.id!)}>Use this</button>
                )}
              </dd>
              {line.also.length > 0 && (
                <div className="mt-1 text-xs text-gray-600">
                  <span className="text-gray-400">also:</span>
                  <ul className="mt-1 space-y-1">
                    {line.also.map((a, i) => (
                      <li key={a.id ?? `column-${i}`} className="flex flex-wrap items-baseline gap-x-2">
                        <span>{alsoLabel(line.field, a, at)}</span>
                        {picking === line.field && a.id && (
                          <button type="button" className={LINK} disabled={busy} onClick={() => pick(a.id!)}>Use this</button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {canPick(line) && (
                <div className="mt-1 text-xs">
                  {picking === line.field
                    ? <button type="button" className="text-gray-500 hover:underline" disabled={busy} onClick={() => setPicking(null)}>Cancel</button>
                    : <button type="button" className={LINK} disabled={busy} onClick={() => setPicking(line.field)}>Use another</button>}
                </div>
              )}
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
